import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  Timestamp,
  addDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { ExpenseCatalogItem, ExpenseEntry } from '../types/expenseCatalog';

const CATALOG_COLLECTION = 'expense-catalog';
const ENTRIES_COLLECTION = 'expense-entries';

const toDate = (v: any): Date => {
  if (!v) return new Date();
  if (v instanceof Date) return v;
  if (v?.toDate) return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? new Date() : d;
};

// ─── Expense Catalog ────────────────────────────────────────────────────────

const normalizeCatalogDoc = (id: string, raw: any): ExpenseCatalogItem => ({
  id,
  name: String(raw.name || '').trim(),
  defaultUnitPrice: Number(raw.defaultUnitPrice || 0),
  notes: String(raw.notes || '').trim(),
  sortIndex: typeof raw.sortIndex === 'number' ? raw.sortIndex : Number(raw.sortIndex) || 0,
  createdAt: toDate(raw.createdAt),
  updatedAt: toDate(raw.updatedAt),
});

export const expenseCatalogService = {
  async getAll(): Promise<ExpenseCatalogItem[]> {
    const snap = await getDocs(collection(db, CATALOG_COLLECTION));
    return snap.docs
      .map((d) => normalizeCatalogDoc(d.id, d.data()))
      .sort((a, b) => {
        const si = (a.sortIndex ?? 0) - (b.sortIndex ?? 0);
        if (si !== 0) return si;
        return a.name.localeCompare(b.name, 'vi');
      });
  },

  async addItem(item: Omit<ExpenseCatalogItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const now = Timestamp.fromDate(new Date());
    const ref = await addDoc(collection(db, CATALOG_COLLECTION), {
      name: item.name.trim(),
      defaultUnitPrice: Number(item.defaultUnitPrice || 0),
      notes: (item.notes || '').trim(),
      sortIndex: item.sortIndex ?? 0,
      createdAt: now,
      updatedAt: now,
    });
    return ref.id;
  },

  async updateItem(id: string, item: Partial<ExpenseCatalogItem>): Promise<void> {
    const now = Timestamp.fromDate(new Date());
    const ref = doc(db, CATALOG_COLLECTION, id);
    await setDoc(
      ref,
      {
        ...(item.name !== undefined && { name: item.name.trim() }),
        ...(item.defaultUnitPrice !== undefined && { defaultUnitPrice: Number(item.defaultUnitPrice) }),
        ...(item.notes !== undefined && { notes: item.notes.trim() }),
        ...(item.sortIndex !== undefined && { sortIndex: item.sortIndex }),
        updatedAt: now,
      },
      { merge: true },
    );
  },

  async deleteItem(id: string): Promise<void> {
    await deleteDoc(doc(db, CATALOG_COLLECTION, id));
  },
};

// ─── Expense Entries (chi phí hàng ngày) ────────────────────────────────────

const normalizeEntryDoc = (id: string, raw: any): ExpenseEntry => ({
  id,
  date: String(raw.date || raw.month || ''),
  month: String(raw.month || ''),
  expenseCatalogId: String(raw.expenseCatalogId || ''),
  expenseName: String(raw.expenseName || '').trim(),
  unitPrice: Number(raw.unitPrice || 0),
  notes: String(raw.notes || '').trim(),
  createdAt: toDate(raw.createdAt),
  updatedAt: toDate(raw.updatedAt),
});

export const expenseEntryService = {
  async getAll(): Promise<ExpenseEntry[]> {
    const snap = await getDocs(collection(db, ENTRIES_COLLECTION));
    return snap.docs.map((d) => normalizeEntryDoc(d.id, d.data()));
  },

  async addEntry(entry: Omit<ExpenseEntry, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const now = Timestamp.fromDate(new Date());
    const ref = await addDoc(collection(db, ENTRIES_COLLECTION), {
      date: entry.date,
      month: entry.month,
      expenseCatalogId: entry.expenseCatalogId,
      expenseName: entry.expenseName.trim(),
      unitPrice: Number(entry.unitPrice),
      notes: (entry.notes || '').trim(),
      createdAt: now,
      updatedAt: now,
    });
    return ref.id;
  },

  async updateEntry(id: string, entry: Partial<Omit<ExpenseEntry, 'id' | 'createdAt'>>): Promise<void> {
    const now = Timestamp.fromDate(new Date());
    await updateDoc(doc(db, ENTRIES_COLLECTION, id), {
      ...entry,
      updatedAt: now,
    });
  },

  async deleteEntry(id: string): Promise<void> {
    await deleteDoc(doc(db, ENTRIES_COLLECTION, id));
  },
};
